import React from 'react';
import {
    Paper,
    Grid,
    Typography,
    Stack,
    Chip,
    Box,
    Button,
    Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';

const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('de-DE', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    });

const formatDeadline = (dateStr) =>
    new Date(dateStr).toLocaleString('de-DE', {
        dateStyle: 'short',
        timeStyle: 'short',
    });

const MetaRow = ({ icon, children }) => (
    <Stack direction="row" spacing={1} alignItems="center">
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: 18,
            }}
        >
            {icon}
        </Box>
        <Typography variant="body2" color="text.secondary">
            {children}
        </Typography>
    </Stack>
);

const TerminHeader = ({ appointment, isTrainer, activeTeamId, terminId }) => {
    const navigate = useNavigate();

    const showRecurringChip =
        appointment.is_recurring && appointment.typ !== 'Training';

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2.5, md: 4 },
                mb: 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Grid container spacing={4} alignItems="flex-start">
                {/* ── Left column ── */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Stack spacing={2}>
                        {/* Chips */}
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Chip
                                label={appointment.typ}
                                color="primary"
                                size="small"
                                sx={{ fontWeight: 700, letterSpacing: 0.3 }}
                            />
                        </Stack>

                        {/* Title */}
                        <Typography variant="h4" fontWeight={800} lineHeight={1.2}>
                            {appointment.titel}
                        </Typography>

                        {/* Meta info */}
                        <Stack spacing={0.75}>
                            <MetaRow icon={<CalendarTodayIcon fontSize="inherit" />}>
                                {formatDate(appointment.dat)}
                            </MetaRow>

                            <MetaRow icon={<AccessTimeIcon fontSize="inherit" />}>
                                {appointment.start_time}
                                {appointment.end_time && ` – ${appointment.end_time}`}
                            </MetaRow>

                            {appointment.ort && (
                                <MetaRow icon={<PlaceIcon fontSize="inherit" />}>
                                    {appointment.ort}
                                </MetaRow>
                            )}
                        </Stack>

                        {/* Description */}
                        {appointment.beschreibung && (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: 'action.hover',
                                    borderLeft: '4px solid',
                                    borderColor: 'primary.main',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                                    {appointment.beschreibung}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Grid>

                {/* ── Divider (only on desktop) ── */}
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: 'none', md: 'block' }, mx: -1 }}
                />

                {/* ── Right column ── */}
                <Grid
                    size={{ xs: 12, md: 'grow' }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: { xs: 'flex-start', md: 'flex-end' },
                        gap: 2,
                    }}
                >
                    {appointment.deadline && (
                        <Box sx={{ textAlign: { md: 'right' } }}>
                            <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                justifyContent={{ md: 'flex-end' }}
                                sx={{ mb: 0.5 }}
                            >
                                <TimerOutlinedIcon
                                    fontSize="small"
                                    sx={{ color: 'text.secondary', fontSize: 16 }}
                                />
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={700}
                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}
                                >
                                    Anmeldefrist
                                </Typography>
                            </Stack>
                            <Typography variant="h6" color="error" fontWeight={800}>
                                {formatDeadline(appointment.deadline)}
                            </Typography>
                        </Box>
                    )}

                    {isTrainer && appointment.typ === 'Spiel' && (
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SportsSoccerIcon />}
                            onClick={() =>
                                navigate(`/teams/${activeTeamId}/lineup/${terminId}`)
                            }
                            sx={{
                                borderRadius: 2,
                                py: 1.25,
                                px: 3,
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: '0.95rem',
                            }}
                        >
                            Zur Aufstellung
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Paper>
    );
};

export default TerminHeader;